<template>
  <div class="inv-stock-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Inventory Stock Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ totalItems }} Items
        </b-badge>
        <b-badge v-show="!loading" variant="light-primary" class="ml-1">
          {{ categorySections.length }} Categories
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading inventory stock records...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="initReport"
        >Retry</b-button
      >
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Modern Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Inventory Stock Report</span>
              <span class="date-range-text">
                <strong>Generated on {{ printedAt }}</strong>
              </span>
            </div>
            <div class="report-meta-line mt-1">
              <span>Category-wise stock summary</span>
              <span v-if="search" class="ml-1"
                >· Search: “{{ search }}”</span
              >
            </div>
          </div>
        </div>
      </div>

      <div v-if="!categorySections.length" class="text-center py-5 no-data">
        <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
        <p>No inventory items found for the selected criteria</p>
      </div>

      <!-- Category-wise sections -->
      <div
        v-for="(section, sIndex) in categorySections"
        :key="section.id || sIndex"
        class="category-block"
      >
        <div class="category-heading">
          <div class="category-heading__left">
            <span class="category-num">{{ sIndex + 1 }}</span>
            <div>
              <h3 class="category-title">{{ section.name }}</h3>
              <p class="category-sub">
                {{ section.items.length }} item{{
                  section.items.length === 1 ? "" : "s"
                }}
              </p>
            </div>
          </div>
        </div>

        <div class="table-container">
          <table class="ledger-style-table">
            <thead>
              <tr>
                <th class="text-center" style="width: 4%">#</th>
                <th class="text-left" style="width: 16%">Item Name</th>
                <th class="text-center" style="width: 8%">SKU</th>
                <th class="text-left" style="width: 10%">Vendor</th>
                <th v-if="inventoryIuss" class="text-left" style="width: 8%">
                  Brand
                </th>
                <th v-if="inventoryIuss" class="text-left" style="width: 8%">
                  Warranty
                </th>
                <th v-if="inventoryIuss" class="text-center" style="width: 8%">
                  Purchase Date
                </th>
                <th v-if="inventoryIuss" class="text-left" style="width: 10%">
                  Specification
                </th>
                <th class="text-center" style="width: 6%">Qty</th>
                <th
                  v-if="!hideSellingPrice"
                  class="text-right"
                  style="width: 9%"
                >
                  Selling Price
                </th>
                <th
                  v-if="!hidePurchasePrice"
                  class="text-right"
                  style="width: 9%"
                >
                  Purchase Price
                </th>
                <th
                  v-if="!hidePurchasePrice"
                  class="text-right"
                  style="width: 9%"
                >
                  Purchase Value
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in section.items"
                :key="item.id || index"
              >
                <td class="text-center font-weight-bold">{{ index + 1 }}</td>
                <td class="text-left font-weight-bold item-name">
                  {{ (item.name || "-").toUpperCase() }}
                </td>
                <td class="text-center sku-cell">{{ item.sku || "-" }}</td>
                <td class="text-left">
                  {{ vendorName(item.supplierID) }}
                </td>
                <td v-if="inventoryIuss" class="text-left">
                  {{ item.brand || "-" }}
                </td>
                <td v-if="inventoryIuss" class="text-left">
                  {{ item.warranty || "-" }}
                </td>
                <td v-if="inventoryIuss" class="text-center">
                  {{ formatPurchaseDate(item.purchaseDate) }}
                </td>
                <td v-if="inventoryIuss" class="text-left">
                  {{ item.specification || "-" }}
                </td>
                <td class="text-center font-weight-bold">
                  {{ item.quantity != null ? item.quantity : 0 }}
                </td>
                <td v-if="!hideSellingPrice" class="text-right">
                  {{ formatMoney(item.sellingPrice) }}
                </td>
                <td v-if="!hidePurchasePrice" class="text-right">
                  {{ formatMoney(item.purchasePrice) }}
                </td>
                <td v-if="!hidePurchasePrice" class="text-right font-weight-bold">
                  {{ formatMoney(linePurchaseValue(item)) }}
                </td>
              </tr>
              <tr class="category-total-row">
                <td
                  :colspan="labelColspan"
                  class="text-right font-weight-bold"
                >
                  Category Total
                </td>
                <td class="text-center font-weight-bold">
                  {{ section.totalQty }}
                </td>
                <td v-if="!hideSellingPrice" class="text-right font-weight-bold">
                  {{ formatMoney(section.totalSelling) }}
                </td>
                <td v-if="!hidePurchasePrice" class="text-right">—</td>
                <td v-if="!hidePurchasePrice" class="text-right font-weight-bold">
                  {{ formatMoney(section.totalPurchase) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Column totals at end -->
      <div v-if="categorySections.length" class="table-container grand-total-table">
        <table class="ledger-style-table">
          <thead>
            <tr>
              <th class="text-left" :colspan="labelColspan">
                Grand Total
              </th>
              <th class="text-center">Qty</th>
              <th v-if="!hideSellingPrice" class="text-right">Selling Value</th>
              <th v-if="!hidePurchasePrice" class="text-right">Purchase Price</th>
              <th v-if="!hidePurchasePrice" class="text-right">Purchase Value</th>
            </tr>
          </thead>
          <tbody>
            <tr class="grand-total-row">
              <td
                :colspan="labelColspan"
                class="text-right font-weight-bold"
              >
                All Categories ({{ categorySections.length }}) ·
                {{ totalItems }} Items
              </td>
              <td class="text-center font-weight-bold">{{ grandQty }}</td>
              <td v-if="!hideSellingPrice" class="text-right font-weight-bold">
                {{ formatMoney(grandSelling) }}
              </td>
              <td v-if="!hidePurchasePrice" class="text-right">—</td>
              <td v-if="!hidePurchasePrice" class="text-right font-weight-bold">
                {{ formatMoney(grandPurchase) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="page-footer">
        Inventory Stock Report · Category-wise · {{ printedAt }}
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import { mapActions } from "vuex";

export default {
  name: "InventoryStockPrint",
  components: { BButton, BSpinner, BBadge },
  data() {
    return {
      loading: false,
      error: null,
      categorySections: [],
      suppliers: [],
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: 0,
      catID: 0,
      search: "",
      inventoryIuss: false,
      reportDomain: String(this.$store.state.report || "")
        .replace(/_/g, "")
        .toLowerCase(),
    };
  },
  computed: {
    hideSellingPrice() {
      return this.reportDomain === "myskoolpublicschool";
    },
    hidePurchasePrice() {
      return this.reportDomain === "myskoolpublicschool";
    },
    labelColspan() {
      // #, Item, SKU, Vendor (+ optional IUSS cols) before Qty
      return 4 + (this.inventoryIuss ? 4 : 0);
    },
    printedAt() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    totalItems() {
      return this.categorySections.reduce(
        (sum, s) => sum + s.items.length,
        0
      );
    },
    grandQty() {
      return this.categorySections.reduce((sum, s) => sum + s.totalQty, 0);
    },
    grandPurchase() {
      return this.categorySections.reduce(
        (sum, s) => sum + s.totalPurchase,
        0
      );
    },
    grandSelling() {
      return this.categorySections.reduce(
        (sum, s) => sum + s.totalSelling,
        0
      );
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),

    async initReport() {
      const q = this.$route.query || {};
      this.db = q.db || this.$store.state.userData.db;
      this.cID = Number(q.cID || this.$store.state.userData.cId || 0);
      this.catID = Number(q.catID || 0);
      this.search = String(q.search || "")
        .trim()
        .toLowerCase();
      this.inventoryIuss =
        q.iuss === "1" ||
        q.iuss === "true" ||
        String(q.iuss || "").toLowerCase() === "true";

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loading = true;
      this.error = null;
      try {
        await Promise.all([
          this.loadCampusInfo(),
          this.loadSuppliers(),
          this.loadIussSetting(),
        ]);
        await this.loadCategoryWiseStock();
      } catch (e) {
        console.error(e);
        this.error =
          "Failed to retrieve inventory stock report. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    async loadCampusInfo() {
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "campuses?db=" +
            this.db +
            "&cId=" +
            this.cID,
          token: this.$store.state.userData.token,
        });
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res && res.data)
          ? res.data
          : [];
        const match = list.find((c) => Number(c.id) === Number(this.cID));
        if (match) {
          this.campusName =
            match.organizationName ||
            match.orgName ||
            match.name ||
            "MY SKOOL";
          this.campusBranch =
            this.cID == 0 ? "All Campuses" : match.branch || match.name || "";
          this.schoolLogo = match.logo || null;
        }
      } catch (e) {
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
          this.schoolLogo = currentBranch.logo || null;
        }
      }
    },

    async loadIussSetting() {
      if (this.inventoryIuss) return;
      try {
        const res = await this.post({
          url:
            this.$store.state.domain +
            "Settings/getSettingsAll?db=" +
            this.db +
            "&cID=" +
            this.cID,
          body: ["inventory_iuss"],
          token: this.$store.state.userData.token,
          subjects: true,
        });
        const list = Array.isArray(res) ? res : [];
        const s = list.find((el) => el.key === "inventory_iuss");
        if (!s) return;
        if (s.valueBool === true) {
          this.inventoryIuss = true;
          return;
        }
        const v = s.value;
        this.inventoryIuss =
          v === true ||
          v === 1 ||
          String(v).toLowerCase() === "true" ||
          String(v) === "1";
      } catch (e) {
        /* keep query flag */
      }
    },

    async loadSuppliers() {
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "posSupplier?db=" +
            this.db +
            "&cID=" +
            this.cID,
          token: this.$store.state.userData.token,
        });
        this.suppliers = Array.isArray(res)
          ? res
          : Array.isArray(res && res.data)
          ? res.data
          : [];
      } catch (e) {
        this.suppliers = [];
      }
    },

    async loadCategories() {
      const endpoint = this.inventoryIuss
        ? "posCategories/LoadData"
        : "posCategories";
      const res = await this.get({
        url:
          this.$store.state.domain +
          endpoint +
          "?db=" +
          this.db +
          "&cID=" +
          this.cID,
        token: this.$store.state.userData.token,
        returnData: true,
      });
      let list = [];
      if (this.inventoryIuss) {
        let bundle = res || {};
        if (
          res &&
          res.data &&
          !Array.isArray(res.data) &&
          (Array.isArray(res.data.data) ||
            Array.isArray(res.data.categories) ||
            Array.isArray(res.data.subCategories))
        ) {
          bundle = res.data;
        }
        const parents = Array.isArray(bundle.categories)
          ? bundle.categories
          : Array.isArray(res && res.categories)
          ? res.categories
          : [];
        const subs = Array.isArray(bundle.subCategories)
          ? bundle.subCategories
          : Array.isArray(res && res.subCategories)
          ? res.subCategories
          : [];
        list =
          Array.isArray(bundle.data) && bundle.data.length
            ? bundle.data
            : parents.concat(subs);
      } else {
        const raw = res && res.data !== undefined ? res.data : res;
        list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw && raw.data)
          ? raw.data
          : [];
      }
      if (this.catID) {
        return list.filter((c) => Number(c.id) === Number(this.catID));
      }
      return list;
    },

    async loadStockByCat(catID) {
      const res = await this.get({
        url:
          this.$store.state.domain +
          "posStock/LoadByCat?db=" +
          this.db +
          "&cID=" +
          this.cID +
          "&catID=" +
          catID,
        token: this.$store.state.userData.token,
      });
      let rows = Array.isArray(res)
        ? res
        : Array.isArray(res && res.data)
        ? res.data
        : [];
      if (this.search) {
        rows = rows.filter((item) =>
          String(item.name || "")
            .toLowerCase()
            .includes(this.search)
        );
      }
      return rows;
    },

    summarizeItems(items) {
      let totalQty = 0;
      let totalPurchase = 0;
      let totalSelling = 0;
      items.forEach((item) => {
        const qty = Number(item.quantity) || 0;
        const pp = Number(item.purchasePrice) || 0;
        const sp = Number(item.sellingPrice) || 0;
        totalQty += qty;
        totalPurchase += qty * pp;
        totalSelling += qty * sp;
      });
      return { totalQty, totalPurchase, totalSelling };
    },

    async loadCategoryWiseStock() {
      const categories = await this.loadCategories();
      const sections = [];

      for (let i = 0; i < categories.length; i += 1) {
        const cat = categories[i];
        const items = await this.loadStockByCat(cat.id);
        if (!items.length) continue;
        const sorted = items.slice().sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), undefined, {
            sensitivity: "base",
          })
        );
        const totals = this.summarizeItems(sorted);
        sections.push({
          id: cat.id,
          name: cat.name || "Uncategorized",
          items: sorted,
          totalQty: totals.totalQty,
          totalPurchase: totals.totalPurchase,
          totalSelling: totals.totalSelling,
        });
      }

      sections.sort((a, b) =>
        String(a.name).localeCompare(String(b.name), undefined, {
          sensitivity: "base",
        })
      );
      this.categorySections = sections;
    },

    linePurchaseValue(item) {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.purchasePrice) || 0;
      return qty * price;
    },

    vendorName(id) {
      const obj = this.suppliers.find((el) => el.id == id);
      return obj ? obj.name : "—";
    },

    formatMoney(value) {
      const num = Number(value);
      if (Number.isNaN(num)) return "0";
      return num.toLocaleString("en-PK");
    },

    formatPurchaseDate(val) {
      if (!val) return "-";
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return String(val);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return dd + "/" + mm + "/" + yyyy;
    },

    print() {
      window.print();
    },

    close() {
      window.close();
      this.$router.push({ name: "inventory" }).catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
.inv-stock-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }

  .report-meta-line {
    font-size: 0.9rem;
    color: #636e72;
  }
}

.category-block {
  margin-bottom: 2rem;
}

.category-heading {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0;
  padding: 0.85rem 1rem;
  background: #2c3e50 !important;
  color: #ffffff !important;
  border-radius: 10px 10px 0 0;
}

.category-heading__left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}

.category-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff !important;
  font-weight: 800;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.category-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #ffffff !important;
}

.category-sub {
  margin: 0.15rem 0 0;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.85) !important;
  opacity: 1;
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 12px 15px;
    box-sizing: border-box;
    font-size: 0.95rem;
    color: #2d3436;
  }

  thead {
    background: #2c3e50 !important;
    th {
      color: #ffffff !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.85rem;
      letter-spacing: 0.5px;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  tbody tr:hover {
    background: #f1f4ff;
  }

  .sku-cell {
    font-weight: 700;
    color: #2d3436 !important;
  }

  .item-name {
    color: #2d3436 !important;
  }

  .category-total-row td {
    background: #f1f3f5 !important;
    font-weight: 700;
    color: #2d3436 !important;
  }
}

.grand-total-table {
  margin-top: 1.5rem;
}

.grand-total-row td {
  background: #eef2f7 !important;
  font-size: 1rem;
}

.no-data {
  color: #adb5bd;
}

.page-footer {
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid #eee;
  color: #adb5bd;
  font-size: 0.85rem;
}

@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .inv-stock-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 0.8cm 0.6cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .ledger-style-table {
    font-size: 9pt !important;
    width: 100% !important;
    page-break-inside: auto !important;
    break-inside: auto !important;

    thead {
      display: table-header-group !important;
    }

    tr {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }

    .category-total-row td {
      background: #f1f3f5 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    th,
    td {
      padding: 5px 8px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .sku-cell {
      color: #000 !important;
    }
  }

  .report-header-modern {
    margin-bottom: 16px !important;
    padding-bottom: 12px !important;
    border-bottom: 3px solid #000 !important;
    page-break-after: avoid !important;
    break-after: avoid !important;

    .school-logo-img {
      height: 90px !important;
      max-width: 220px !important;
      object-fit: contain !important;
    }

    .school-name-full {
      font-size: 1.4rem !important;
    }
  }

  .category-block {
    margin-bottom: 14px !important;
    page-break-inside: auto !important;
    break-inside: auto !important;
    page-break-before: auto !important;
    break-before: auto !important;
  }

  .category-heading {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    background: #2c3e50 !important;
    color: #ffffff !important;
    page-break-after: avoid !important;
    break-after: avoid !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  .category-title,
  .category-num {
    color: #ffffff !important;
  }

  .category-sub {
    color: rgba(255, 255, 255, 0.85) !important;
  }

  .table-container {
    page-break-inside: auto !important;
    break-inside: auto !important;
  }

  .grand-total-table {
    margin-top: 10px !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  .grand-total-row td {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    background: #eef2f7 !important;
  }

  .page-footer {
    display: none !important;
  }
}
</style>
